import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Alert, Button } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Pagination, Tabs, Modal, Dropdown } from '../../components/Common';
import { PageHeader } from '../../components/Layout';
import { DiscountsTable } from '../../components/Tables';
import { AddDiscountForm, EditDiscountForm } from '../../components/Forms/Discount';
import { discountsService, DiscountDisplay, DiscountsPagination, VendorsListingService, categoryService } from '../../services';
import { Category } from '../../types/category.types';
import { Vendor } from '../../types';
import { handleApiError, handleApiSuccess } from '../../services/error.service';
import { useSmartPageSize } from '../../config/pagination';

const Discounts: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'archived'>('all');
  const [selectedPromocode, setSelectedPromocode] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPromocode, setEditingPromocode] = useState<DiscountDisplay | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('All categories');
  const [categoryAnchor, setCategoryAnchor] = useState<null | HTMLElement>(null);
  const [vendorFilter, setVendorFilter] = useState<string>('All vendors'); // Store vendor ID or 'All vendors'
  const [vendorAnchor, setVendorAnchor] = useState<null | HTMLElement>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  
  // API state
  const [promoCodes, setPromoCodes] = useState<DiscountDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<DiscountsPagination>({
    hasMore: false,
    currentPage: 1,
    totalItems: 0,
    totalPages: 1,
  });
  
  // Smart pagination based on screen size
  const smartPageSize = useSmartPageSize('promoCodes');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(smartPageSize);

  // Fetch vendors for filter
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const vendorListing = await VendorsListingService.getVendorsListing(1, 1000, '', 'active');
        setVendors(vendorListing.vendors);
      } catch (err) {
        console.error('Error fetching vendors:', err);
        setVendors([]);
      }
    };
    fetchVendors();
  }, []);

  // Fetch categories for filter
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryService.getAllCategories();
        if (response.data?.categories) {
          setCategories(response.data.categories);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // Fetch promo codes from API
  const fetchPromoCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Only add status if not 'all' tab
      const status = activeTab === 'all' ? undefined : activeTab === 'active' ? 'Active' : 'Archived';
      
      // Convert filters to arrays (only if not "All")
      const categories = categoryFilter !== 'All categories' ? [categoryFilter] : undefined;
      const vendors = vendorFilter !== 'All vendors' ? [vendorFilter] : undefined;
      
      const result = await discountsService.getDiscounts(status, currentPage, itemsPerPage, categories, vendors);
      
      setPromoCodes(result.discounts);
      setPagination(result.pagination);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, itemsPerPage, categoryFilter, vendorFilter]);

  useEffect(() => {
    fetchPromoCodes();
  }, [activeTab, currentPage, itemsPerPage, categoryFilter, vendorFilter, fetchPromoCodes]);

  // Reset to page 1 when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, vendorFilter]);

  // Extract categories from category service
  const availableCategories = useMemo(() => {
    const categoriesList = categories.map(category => category.name).sort();
    return ['All categories', ...categoriesList];
  }, [categories]);

  // Get category names for forms (without "All categories")
  const formCategories = useMemo(() => {
    return categories.map(category => category.name).sort();
  }, [categories]);

  // Extract unique vendors from all vendors (for dropdown options)
  // Note: We show all approved vendors, not just those in current promoCodes
  // since filtering is done on the backend
  const availableVendors = useMemo(() => {
    const vendorsList = vendors.map(vendor => ({
      id: vendor.vendorId,
      label: vendor.companyName || vendor.contactName,
    })).sort((a, b) => a.label.localeCompare(b.label));

    return [{ id: 'All vendors', label: 'All vendors' }, ...vendorsList];
  }, [vendors]);


  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingPromocode(null);
  };

  const handleSaveChanges = async (data: any) => {
    if (!selectedPromocode || !editingPromocode) return;
    
    try {
      // Try using 'value' - backend should handle DynamoDB reserved keyword with ExpressionAttributeNames
      const updates: any = {
        value: parseFloat(data.discount),
        type: data.discountType === '%' ? 'percent' : 'number',
      };
      
      if (data.categories && data.categories.length > 0) {
        updates.categories = data.categories;
      } else {
        updates.categories = [];
      }
      
      if (data.expiresOn && data.expiresOn.trim() !== '') {
        updates.expires = new Date(data.expiresOn).toISOString();
      }
      
      if (data.maxUsage && data.maxUsage.trim() !== '') {
        updates.maxUsage = parseInt(data.maxUsage, 10);
      }
      
      if (data.vendors && data.vendors.length > 0) {
        updates.vendors = data.vendors;
      } else {
        updates.vendors = [];
      }
      
      console.log('Updating discount with data:', updates);
      await discountsService.updateDiscount(selectedPromocode, updates);
      handleApiSuccess('Promo code updated successfully');
      handleEditDialogClose();
      fetchPromoCodes();
    } catch (err) {
      handleApiError(err);
      throw err; // Re-throw to let form handle loading state
    }
  };

  const handleArchive = async (promoCodeId: string) => {
    if (!promoCodeId) return;
    
    try {
      await discountsService.archiveDiscount(promoCodeId);
      handleApiSuccess('Promo code archived successfully');
      setSelectedPromocode(null);
      fetchPromoCodes();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCreate = async (data: any) => {
    try {
      const promoCodeData: any = {
        code: data.code.toUpperCase(),
        value: parseFloat(data.discount),
        type: data.discountType === '%' ? 'percent' : 'number',
        status: 'Active',
      };
      
      if (data.categories && data.categories.length > 0) {
        promoCodeData.categories = data.categories;
      }
      
      if (data.expiresOn && data.expiresOn.trim() !== '') {
        promoCodeData.expires = new Date(data.expiresOn).toISOString();
      }
      
      if (data.maxUsage && data.maxUsage.trim() !== '') {
        promoCodeData.maxUsage = parseInt(data.maxUsage, 10);
      }
      
      if (data.vendors && data.vendors.length > 0) {
        promoCodeData.vendors = data.vendors;
      }
      
      console.log('Creating discount with data:', promoCodeData);
      await discountsService.createDiscount(promoCodeData);
      handleApiSuccess('Promo code created successfully');
      handleDialogClose();
      fetchPromoCodes();
    } catch (err: any) {
      // Check if it's a 409 conflict error (promo code already exists)
      if (err.response?.status === 409) {
        handleApiError(new Error(`Promo code "${data.code.toUpperCase()}" already exists. Please use a different code.`));
      } else {
        handleApiError(err);
      }
      throw err; // Re-throw to let form handle loading state
    }
  };

  return (
    <Box
      className="page-container"
      sx={{
      padding: {
        xs: '24px 16px',
        sm: '40px 40px',
        md: '40px 80px',
      },
        gap: '24px',
      width: '100%',
        height: '100%',
      boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto', // Changed from 'hidden' to 'auto' to enable scrolling
      }}
    >
      {/* Page Header */}
      <PageHeader
        title="Discounts"
        actionButtonText="Create New Promocode"
        onActionClick={handleDialogOpen}
      />

      {/* Navigation Tabs */}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0
      }}>
        <Tabs
          tabs={[
            { key: 'all', label: 'All' },
            { key: 'active', label: 'Active' },
            { key: 'archived', label: 'Archived' },
          ]}
          activeTab={activeTab}
          onTabChange={(tabKey) => setActiveTab(tabKey as typeof activeTab)}
        />

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 'var(--palette-light-2-value)', flexWrap: 'wrap' }}>
          {/* Category Filter */}
          <Box>
          <Button
              onClick={(e) => setCategoryAnchor(e.currentTarget)}
            sx={{
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                color: 'var(--palette-light-text-primary-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              height: '39px',
              padding: '10px 20px 7px',
              borderRadius: '16px',
              textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                maxWidth: '200px',
              '&:hover': {
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                opacity: 0.9,
              },
                '& .MuiButton-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
              },
            }}
          >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                {categoryFilter}
              </span>
              <KeyboardArrowDownIcon sx={{ fontSize: '20px', flexShrink: 0 }} />
          </Button>
            <Dropdown
              anchorEl={categoryAnchor}
              open={Boolean(categoryAnchor)}
              onClose={() => setCategoryAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              options={availableCategories.map(category => ({
                label: category,
                value: category,
                onClick: () => {
                  setCategoryFilter(category);
                  setCategoryAnchor(null);
                  setCurrentPage(1); // Reset to page 1 when filter changes
                },
              }))}
            />
        </Box>

          {/* Vendor Filter */}
          <Box>
        <Button
              onClick={(e) => setVendorAnchor(e.currentTarget)}
          sx={{
            backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            color: 'var(--palette-light-text-primary-value-hex)',
            fontSize: 'var(--palette-light-font-size-default-value)',
            height: '39px',
            padding: '10px 20px 7px',
            borderRadius: '16px',
            textTransform: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
                maxWidth: '200px',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              opacity: 0.9,
            },
                '& .MuiButton-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
            },
          }}
        >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                {availableVendors.find(v => v.id === vendorFilter)?.label || vendorFilter}
              </span>
              <KeyboardArrowDownIcon sx={{ fontSize: '20px', flexShrink: 0 }} />
        </Button>
            <Dropdown
              anchorEl={vendorAnchor}
              open={Boolean(vendorAnchor)}
              onClose={() => setVendorAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              options={availableVendors.map(vendor => ({
                label: vendor.label,
                value: vendor.id,
                onClick: () => {
                  setVendorFilter(vendor.id);
                  setVendorAnchor(null);
                  setCurrentPage(1); // Reset to page 1 when filter changes
                },
              }))}
            />
          </Box>
        </Box>
          </Box>

        {/* Error State */}
        {error && !loading && (
          <Box sx={{ padding: '40px' }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

      {/* PromoCodes Table */}
              <Box sx={{
        backgroundColor: 'var(--palette-light-background-primary-value-hex)', 
        width: '100%',
        flex: 1,
                display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // Allow flex child to shrink
        overflow: 'auto' // Enable scrolling within table container
      }}>
        <DiscountsTable
          promoCodes={promoCodes}
          loading={loading}
          isArchivedTab={activeTab === 'archived'}
          onEdit={async (promoCode) => {
            setEditingPromocode(promoCode);
            setSelectedPromocode(promoCode.id);
            setEditDialogOpen(true);
          }}
          onArchive={async (promoCodeId) => {
            await handleArchive(promoCodeId);
          }}
        />
      </Box>

      {/* Pagination */}
      {!loading && !error && pagination.totalPages > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: 'var(--palette-light-6-value)', // 24px
          mt: 'var(--palette-light-6-value)', // 24px
        }}>
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            itemLabel="promocodes"
            showItemCount={true}
            showPrevNext={true}
          />
        </Box>
      )}

      {/* Create Promocode Modal */}
      <Modal
        open={dialogOpen}
        onClose={handleDialogClose}
        title="New promocode"
        width={508}
      >
        <AddDiscountForm
          categories={formCategories}
          vendors={vendors}
          onSubmit={handleCreate}
          onCancel={handleDialogClose}
        />
      </Modal>

      {/* Edit Promocode Modal */}
      {editingPromocode && (
        <Modal
        open={editDialogOpen}
        onClose={handleEditDialogClose}
          title="Edit promocode"
          width={508}
        >
          <EditDiscountForm
            discountId={editingPromocode.id}
            initialCode={editingPromocode.code}
            initialDiscount={editingPromocode.discountValue.toString()}
            initialDiscountType={editingPromocode.discountType === 'percent' ? '%' : '$'}
            initialCategories={editingPromocode.categories}
            initialExpiresOn={editingPromocode.expires === 'Never' ? '' : editingPromocode.expires}
            initialMaxUsage={editingPromocode.maxUsage?.toString() || ''}
            initialVendors={editingPromocode.vendors || []}
            categories={formCategories}
            vendors={vendors}
            onSubmit={handleSaveChanges}
            onCancel={handleEditDialogClose}
          />
        </Modal>
      )}
    </Box>
  );
};

export default Discounts;
